import { QueryDocumentSnapshot } from 'firebase/firestore';
import { Block, ApartmentUnit, Parking, Amenity } from '@/types/resources/property';
import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  limit,
  startAfter,
  setDoc,
  arrayUnion,
  writeBatch
} from 'firebase/firestore';
import { addWorkspaceAndActivity, createActivityRecord, createDetailedActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
import { User } from '@/types/users';
import { removeBlockStats } from './workspaces';
import { removeUndefined } from '@/utils/removeUndefined';
import { loadMockData } from '@/mock';

// Constants
const BLOCKS_COLLECTION = 'blocks';
const APARTMENT_UNITS_COLLECTION = 'apartment_units';
const PARKINGS_COLLECTION = 'parkings';
const AMENITIES_COLLECTION = 'amenities';

// Types
export interface PropertiesQueryParams {
  searchTerm?: string;
  pageSize?: number;
  currentPage?: number;
  lastDoc?: QueryDocumentSnapshot;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  firestoreUser: User;
}

// Helper function to filter mock data based on search term
function filterMockData<T extends { name?: string; number?: string; description?: string }>(
  data: T[],
  searchTerm?: string
): T[] {
  if (!searchTerm) return data;
  
  return data.filter(item => {
    const searchFields = [
      item.name || '',
      item.number || '',
      item.description || ''
    ].map(field => field.toLowerCase());
    
    const searchLower = searchTerm.toLowerCase();
    return searchFields.some(field => field.includes(searchLower));
  });
}

// Helper function to simulate pagination for mock data
function paginateMockData<T>(
  data: T[],
  pageSize?: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  if (!pageSize) {
    return { data, total: data.length };
  }

  // Calculate start index based on current page
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);

  return {
    data: paginatedData,
    total: data.length
  };
}

// Block API
export async function getBlocks({
  searchTerm,
  pageSize,
  currentPage,
  firestoreUser
}: PropertiesQueryParams): Promise<{ blocks: Block[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  console.log('Fetching blocks from Firestore for workspace:', firestoreUser?.workspace?.id);
  
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    const mockBlocks = await loadMockData<Block[]>('blocks', firestoreUser.language);
    const filteredData = filterMockData(mockBlocks, searchTerm);
    const paginatedData = paginateMockData(filteredData, pageSize, currentPage || 1);
    
    // Use the actual filtered data length for total count
    const total = filteredData.length;
    
    return {
      blocks: paginatedData.data,
      lastDoc: undefined,
      total: total
    };
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }
  
    const blocksRef = collection(db, BLOCKS_COLLECTION);
    const constraints = [where('workspace.id', '==', firestoreUser.workspace.id)];
    
    if (searchTerm) {
      constraints.push(
        where('name', '>=', searchTerm),
        where('name', '<=', searchTerm + '\uf8ff')
      );
    }
    
    // Get paginated data
    let q = query(blocksRef, ...constraints);
    
    if (pageSize) {
      q = query(q, limit(pageSize));
    }
    
    const snapshot = await getDocs(q);
    const blocks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Block));
    
    // Get total count from workspace stats
    const workspaceRef = doc(db, 'workspaces', firestoreUser.workspace.id);
    const workspaceDoc = await getDoc(workspaceRef);
    let total = blocks.length; // fallback
    
    if (workspaceDoc.exists()) {
      const workspaceData = workspaceDoc.data();
      if (workspaceData.stats?.blocks) {
        total = Object.keys(workspaceData.stats.blocks).length;
      }
    }
    
    return {
      blocks,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total: total
    };
  } catch (error) {
    console.error('Error fetching blocks:', error);
    throw new Error('Failed to fetch blocks');
  }
}

export async function getBlockById(id: string, firestoreUser: User): Promise<Block | null> {
  console.log('Fetching block by ID from Firestore:', id);
  
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    const mockBlocks = await loadMockData<Block[]>('blocks', firestoreUser.language);
    const mockBlock = mockBlocks.find(block => block.id === id);
    return mockBlock || null;
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the block document directly by ID
    const blockRef = doc(db, BLOCKS_COLLECTION, id);
    const blockDoc = await getDoc(blockRef);
    
    if (!blockDoc.exists()) {
      return null;
    }

    const blockData = blockDoc.data();
    
    // Check if the block belongs to the current workspace
    if (blockData.workspace.id !== firestoreUser.workspace.id) {
      return null;
    }

    return { ...blockData, id: blockDoc.id } as Block;
  } catch (error) {
    console.error('Error fetching block:', error);
    throw new Error('Failed to fetch block');
  }
}

export async function addBlock(block: Omit<Block, 'id' | 'activityRecords' | 'workspace'>, firestoreUser: User): Promise<string> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 500));
    return `demo-block-${Date.now()}`;
  }
  
  try {
    const blockData = addWorkspaceAndActivity(block, firestoreUser, 'created');
    const cleanedBlock = removeUndefined(blockData);
    
    const blockRef = doc(collection(db, BLOCKS_COLLECTION));
    await setDoc(blockRef, cleanedBlock);
    return blockRef.id;
  } catch (error) {
    console.error('Error adding block:', error);
    throw new Error('Failed to add block');
  }
}

// Bulk upload blocks using Firestore batch operations
export async function addBlocksBulk(
  blocks: Omit<Block, 'id' | 'activityRecords' | 'workspace'>[], 
  firestoreUser: User,
  onProgress?: (uploaded: number, total: number) => void
): Promise<{ successCount: number; errorCount: number; errors: string[] }> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate progress for demo mode
    for (let i = 0; i < blocks.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      onProgress?.(i + 1, blocks.length);
    }
    return { successCount: blocks.length, errorCount: 0, errors: [] };
  }
  
  try {
    const CHUNK_SIZE = 500; // Firestore batch limit
    const chunks = [];
    
    // Split records into chunks of 500
    for (let i = 0; i < blocks.length; i += CHUNK_SIZE) {
      chunks.push(blocks.slice(i, i + CHUNK_SIZE));
    }

    let totalUploaded = 0;
    const errors: string[] = [];
    
    // Process each chunk with progress updates
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      const batch = writeBatch(db);
      
      chunk.forEach((blockData) => {
        const blockRef = doc(collection(db, BLOCKS_COLLECTION));
        const blockWithMetadata = addWorkspaceAndActivity(blockData, firestoreUser, 'created');
        const cleanedBlock = removeUndefined(blockWithMetadata);
        
        batch.set(blockRef, cleanedBlock);
      });

      try {
        await batch.commit();
        totalUploaded += chunk.length;
        onProgress?.(totalUploaded, blocks.length);
      } catch (error) {
        console.error(`Error uploading chunk ${chunkIndex + 1}:`, error);
        errors.push(`Chunk ${chunkIndex + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    const successCount = totalUploaded;
    const errorCount = blocks.length - totalUploaded;

    return { successCount, errorCount, errors };
  } catch (error) {
    console.error('Bulk upload error:', error);
    throw new Error('Failed to upload blocks in bulk');
  }
}

// Bulk upload apartment units using Firestore batch operations
export async function addApartmentUnitsBulk(
  units: Omit<ApartmentUnit, 'id' | 'workspace' | 'activityRecords'>[], 
  firestoreUser: User,
  onProgress?: (uploaded: number, total: number) => void
): Promise<{ successCount: number; errorCount: number; errors: string[] }> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate progress for demo mode
    for (let i = 0; i < units.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      onProgress?.(i + 1, units.length);
    }
    return { successCount: units.length, errorCount: 0, errors: [] };
  }
  
  try {
    const CHUNK_SIZE = 500; // Firestore batch limit
    const chunks = [];
    
    // Split records into chunks of 500
    for (let i = 0; i < units.length; i += CHUNK_SIZE) {
      chunks.push(units.slice(i, i + CHUNK_SIZE));
    }

    let totalUploaded = 0;
    const errors: string[] = [];
    
    // Process each chunk with progress updates
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      const batch = writeBatch(db);
      
      chunk.forEach((unitData) => {
        const unitRef = doc(collection(db, APARTMENT_UNITS_COLLECTION));
        const unitWithMetadata = addWorkspaceAndActivity(unitData, firestoreUser, 'created');
        const cleanedUnit = removeUndefined(unitWithMetadata);
        
        batch.set(unitRef, cleanedUnit);
      });

      try {
        await batch.commit();
        totalUploaded += chunk.length;
        onProgress?.(totalUploaded, units.length);
      } catch (error) {
        console.error(`Error uploading chunk ${chunkIndex + 1}:`, error);
        errors.push(`Chunk ${chunkIndex + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    const successCount = totalUploaded;
    const errorCount = units.length - totalUploaded;

    return { successCount, errorCount, errors };
  } catch (error) {
    console.error('Bulk upload error:', error);
    throw new Error('Failed to upload apartment units in bulk');
  }
}

// Bulk upload parking units using Firestore batch operations
export async function addParkingBulk(
  parkingUnits: Omit<Parking, 'id' | 'workspace' | 'activityRecords'>[], 
  firestoreUser: User,
  onProgress?: (uploaded: number, total: number) => void
): Promise<{ successCount: number; errorCount: number; errors: string[] }> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate progress for demo mode
    for (let i = 0; i < parkingUnits.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      onProgress?.(i + 1, parkingUnits.length);
    }
    return { successCount: parkingUnits.length, errorCount: 0, errors: [] };
  }
  
  try {
    const CHUNK_SIZE = 500; // Firestore batch limit
    const chunks = [];
    
    // Split records into chunks of 500
    for (let i = 0; i < parkingUnits.length; i += CHUNK_SIZE) {
      chunks.push(parkingUnits.slice(i, i + CHUNK_SIZE));
    }

    let totalUploaded = 0;
    const errors: string[] = [];
    
    // Process each chunk with progress updates
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      const batch = writeBatch(db);
      
      chunk.forEach((parkingData) => {
        const parkingRef = doc(collection(db, PARKINGS_COLLECTION));
        const parkingWithMetadata = addWorkspaceAndActivity(parkingData, firestoreUser, 'created');
        const cleanedParking = removeUndefined(parkingWithMetadata);
        
        batch.set(parkingRef, cleanedParking);
      });

      try {
        await batch.commit();
        totalUploaded += chunk.length;
        onProgress?.(totalUploaded, parkingUnits.length);
      } catch (error) {
        console.error(`Error uploading chunk ${chunkIndex + 1}:`, error);
        errors.push(`Chunk ${chunkIndex + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    const successCount = totalUploaded;
    const errorCount = parkingUnits.length - totalUploaded;

    return { successCount, errorCount, errors };
  } catch (error) {
    console.error('Bulk upload error:', error);
    throw new Error('Failed to upload parking units in bulk');
  }
}

export async function updateBlock(id: string, data: Partial<Block>, firestoreUser: User): Promise<void> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  }
  
  try {
    const docRef = doc(db, BLOCKS_COLLECTION, id);
    const blockDoc = await getDoc(docRef);

    if (!blockDoc.exists()) {
      throw new Error('Block not found');
    }

    const existingData = blockDoc.data() as Block;
    const activityRecord = createDetailedActivityRecord('updated', existingData, data, firestoreUser);

    // Remove activityRecords from data to avoid conflicts
    const { activityRecords, ...updateData } = data;
    
    // Prepare the update data without activityRecords
    const cleanData = removeUndefined(updateData);
    
    // Add activity record separately
    const updatePayload = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating block:', error);
    throw new Error('Failed to update block');
  }
}

export async function deleteBlock(id: string, firestoreUser: User): Promise<void> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const blockRef = doc(db, BLOCKS_COLLECTION, id);
    const blockDoc = await getDoc(blockRef);

    if (!blockDoc.exists()) {
      throw new Error('Block not found');
    }

    const blockData = blockDoc.data();
    if (blockData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Block not found in current workspace');
    }

    // Check for dependencies before deletion
    const dependencies: { type: string; items: any[] }[] = [];

    // Check for apartment units in this block
    const unitsRef = collection(db, APARTMENT_UNITS_COLLECTION);
    const unitsQuery = query(
      unitsRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('block.id', '==', id)
    );
    const unitsSnapshot = await getDocs(unitsQuery);
    
    if (!unitsSnapshot.empty) {
      const units = unitsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dependencies.push({
        type: 'apartmentUnits',
        items: units
      });
    }

    // Check for parking spaces in this block
    const parkingsRef = collection(db, PARKINGS_COLLECTION);
    const parkingsQuery = query(
      parkingsRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('block.id', '==', id)
    );
    const parkingsSnapshot = await getDocs(parkingsQuery);
    
    if (!parkingsSnapshot.empty) {
      const parkings = parkingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dependencies.push({
        type: 'parkings',
        items: parkings
      });
    }

    // Check for amenities in this block
    const amenitiesRef = collection(db, AMENITIES_COLLECTION);
    const amenitiesQuery = query(
      amenitiesRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('block.id', '==', id)
    );
    const amenitiesSnapshot = await getDocs(amenitiesQuery);
    
    if (!amenitiesSnapshot.empty) {
      const amenities = amenitiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dependencies.push({
        type: 'amenities',
        items: amenities
      });
    }

    // If there are dependencies, throw detailed error
    if (dependencies.length > 0) {
      const error = new Error('Cannot delete block with dependencies');
      (error as any).dependencies = dependencies;
      (error as any).blockName = blockData.name;
      throw error;
    }

    const activityRecord = createActivityRecord('deleted', blockData, firestoreUser);

    // Add the activity record before deletion
    await updateDoc(blockRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    // Remove block stats from workspace stats
    try {
      await removeBlockStats(firestoreUser, id);
    } catch (error) {
      console.warn('Failed to remove block stats:', error);
      // Don't fail the entire operation if stats update fails
    }

    await deleteDoc(blockRef);
  } catch (error) {
    console.error('Error deleting block:', error);
    throw error;
  }
}

export async function forceDeleteBlock(id: string, firestoreUser: User): Promise<void> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    return;
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const blockRef = doc(db, BLOCKS_COLLECTION, id);
    const blockDoc = await getDoc(blockRef);

    if (!blockDoc.exists()) {
      throw new Error('Block not found');
    }

    const blockData = blockDoc.data();
    if (blockData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Block not found in current workspace');
    }

    // Helper function to process documents in chunks
    const processDocumentsInChunks = async (
      documents: any[],
      collectionName: string,
      processDocument: (doc: any, batch: any) => void
    ) => {
      if (documents.length === 0) return;
      
      const CHUNK_SIZE = 500; // Firestore batch limit
      const chunks = [];
      
      // Split documents into chunks
      for (let i = 0; i < documents.length; i += CHUNK_SIZE) {
        chunks.push(documents.slice(i, i + CHUNK_SIZE));
      }
      
      console.log(`Processing ${documents.length} ${collectionName} in ${chunks.length} chunks`);
      
      // Process each chunk
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex];
        const batch = writeBatch(db);
        
        chunk.forEach(doc => {
          processDocument(doc, batch);
        });
        
        await batch.commit();
        console.log(`Processed chunk ${chunkIndex + 1}/${chunks.length} for ${collectionName}`);
      }
    };

    // Step 1: Delete all parking spaces in this block
    const parkingsRef = collection(db, PARKINGS_COLLECTION);
    const parkingsQuery = query(
      parkingsRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('block.id', '==', id)
    );
    const parkingsSnapshot = await getDocs(parkingsQuery);
    
    if (!parkingsSnapshot.empty) {
      const parkingDocs = parkingsSnapshot.docs.map(doc => ({ ref: doc.ref, data: doc.data() }));
      
      await processDocumentsInChunks(parkingDocs, 'parking spaces', (doc, batch) => {
        const activityRecord = createActivityRecord('deleted', doc.data, firestoreUser);
        
        // Add activity record before deletion
        batch.update(doc.ref, {
          activityRecords: arrayUnion(activityRecord)
        });
        
        // Delete the parking document
        batch.delete(doc.ref);
      });
      
      console.log(`Deleted ${parkingsSnapshot.docs.length} parking spaces`);
    }

    // Step 2: Delete all apartment units in this block
    const unitsRef = collection(db, APARTMENT_UNITS_COLLECTION);
    const unitsQuery = query(
      unitsRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('block.id', '==', id)
    );
    const unitsSnapshot = await getDocs(unitsQuery);
    
    if (!unitsSnapshot.empty) {
      const unitDocs = unitsSnapshot.docs.map(doc => ({ ref: doc.ref, data: doc.data() }));
      
      await processDocumentsInChunks(unitDocs, 'apartment units', (doc, batch) => {
        const activityRecord = createActivityRecord('deleted', doc.data, firestoreUser);
        
        // Add activity record before deletion
        batch.update(doc.ref, {
          activityRecords: arrayUnion(activityRecord)
        });
        
        // Delete the apartment unit document
        batch.delete(doc.ref);
      });
      
      console.log(`Deleted ${unitsSnapshot.docs.length} apartment units`);
    }

    // Step 3: Delete all amenities in this block
    const amenitiesRef = collection(db, AMENITIES_COLLECTION);
    const amenitiesQuery = query(
      amenitiesRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('block.id', '==', id)
    );
    const amenitiesSnapshot = await getDocs(amenitiesQuery);
    
    if (!amenitiesSnapshot.empty) {
      const amenityDocs = amenitiesSnapshot.docs.map(doc => ({ ref: doc.ref, data: doc.data() }));
      
      await processDocumentsInChunks(amenityDocs, 'amenities', (doc, batch) => {
        const activityRecord = createActivityRecord('deleted', doc.data, firestoreUser);
        
        // Add activity record before deletion
        batch.update(doc.ref, {
          activityRecords: arrayUnion(activityRecord)
        });
        
        // Delete the amenity document
        batch.delete(doc.ref);
      });
      
      console.log(`Deleted ${amenitiesSnapshot.docs.length} amenities`);
    }

    // Step 4: Finally delete the block itself
    const activityRecord = createActivityRecord('deleted', blockData, firestoreUser);

    // Add the activity record before deletion
    await updateDoc(blockRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    // Remove block stats from workspace stats
    try {
      await removeBlockStats(firestoreUser, id);
    } catch (error) {
      console.warn('Failed to remove block stats:', error);
      // Don't fail the entire operation if stats update fails
    }

    await deleteDoc(blockRef);
    console.log(`Block "${blockData.name}" and all related data deleted successfully`);
    
  } catch (error) {
    console.error('Error force deleting block:', error);
    throw error;
  }
}

// ApartmentUnit API
export async function getApartmentUnits({
  searchTerm,
  pageSize,
  currentPage,
  lastDoc,
  firestoreUser
}: PropertiesQueryParams): Promise<{ apartmentUnits: ApartmentUnit[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    const mockUnits = await loadMockData<ApartmentUnit[]>('units', firestoreUser.language);
    const filteredData = filterMockData(mockUnits, searchTerm);
    const paginatedData = paginateMockData(filteredData, pageSize, currentPage || 1);
    
    // Use the actual filtered data length for total count
    const total = filteredData.length;
    
    return {
      apartmentUnits: paginatedData.data,
      lastDoc: undefined,
      total: total
    };
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }
  
    const unitsRef = collection(db, APARTMENT_UNITS_COLLECTION);
    const constraints = [where('workspace.id', '==', firestoreUser.workspace.id)];
    
    if (searchTerm) {
      constraints.push(
        where('number', '>=', searchTerm),
        where('number', '<=', searchTerm + '\uf8ff')
      );
    }
    
    // Get total count first
    const countQuery = query(unitsRef, ...constraints);
    const countSnapshot = await getDocs(countQuery);
    const total = countSnapshot.size;
    
    // Get paginated data
    let q = query(unitsRef, ...constraints);
    
    if (pageSize) {
      q = query(q, limit(pageSize));
    }
    
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    const snapshot = await getDocs(q);
    const apartmentUnits = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ApartmentUnit));
    
    return {
      apartmentUnits,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total: total
    };
  } catch (error) {
    console.error('Error fetching apartment units:', error);
    throw new Error('Failed to fetch apartment units');
  }
}

export async function getApartmentUnitById(id: string, firestoreUser: User): Promise<ApartmentUnit | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    const mockUnits = await loadMockData<ApartmentUnit[]>('units', firestoreUser.language);
    const mockUnit = mockUnits.find(unit => unit.id === id);
    return mockUnit || null;
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the apartment unit document directly by ID
    const unitRef = doc(db, APARTMENT_UNITS_COLLECTION, id);
    const unitDoc = await getDoc(unitRef);
    
    if (!unitDoc.exists()) {
      return null;
    }

    const unitData = unitDoc.data();
    
    // Check if the unit belongs to the current workspace
    if (unitData.workspace.id !== firestoreUser.workspace.id) {
      return null;
    }

    return { ...unitData, id: unitDoc.id } as ApartmentUnit;
  } catch (error) {
    console.error('Error fetching apartment unit:', error);
    throw new Error('Failed to fetch apartment unit');
  }
}

export async function addApartmentUnit(unit: Omit<ApartmentUnit, 'id' | 'activityRecords' | 'workspace'>, firestoreUser: User): Promise<string> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 500));
    return `demo-unit-${Date.now()}`;
  }
  
  try {
    const unitData = addWorkspaceAndActivity(unit, firestoreUser, 'created');
    const cleanedUnit = removeUndefined(unitData);
    
    const unitRef = doc(collection(db, APARTMENT_UNITS_COLLECTION));
    await setDoc(unitRef, cleanedUnit);
    return unitRef.id;
  } catch (error) {
    console.error('Error adding apartment unit:', error);
    throw new Error('Failed to add apartment unit');
  }
}

export async function updateApartmentUnit(id: string, data: Partial<ApartmentUnit> & { [key: string]: any }, firestoreUser: User): Promise<void> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  }
  
  try {
    const docRef = doc(db, APARTMENT_UNITS_COLLECTION, id);
    const unitDoc = await getDoc(docRef);

    if (!unitDoc.exists()) {
      throw new Error('Apartment unit not found');
    }

    const existingData = unitDoc.data() as ApartmentUnit;
    
    // Separate deleteField values from regular data for activity record
    const deleteFields: string[] = [];
    const regularData: any = {};
    
    Object.keys(data).forEach(key => {
      const value = (data as any)[key];
      if (value && typeof value === 'object' && value._methodName === 'FieldValue.delete') {
        deleteFields.push(key);
      } else {
        regularData[key] = value;
      }
    });
    
    // Create activity record only with regular data (not deleteField values)
    const activityRecord = createDetailedActivityRecord('updated', existingData, regularData, firestoreUser);
    
    // Clean the activity record to remove any undefined values
    const cleanActivityRecord = removeUndefined(activityRecord);

    // Remove activityRecords from data to avoid conflicts
    const { activityRecords, ...updateData } = data;
    
    // Prepare the update payload - include both regular data and deleteField values
    const updatePayload: any = {
      activityRecords: arrayUnion(cleanActivityRecord)
    };
    
    // Add regular fields
    Object.keys(updateData).forEach(key => {
      const value = (updateData as any)[key];
      if (value !== undefined) {
        updatePayload[key] = value;
      }
    });

    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating apartment unit:', error);
    throw new Error('Failed to update apartment unit');
  }
}

export async function deleteApartmentUnit(id: string, firestoreUser: User): Promise<void> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const unitRef = doc(db, APARTMENT_UNITS_COLLECTION, id);
    const unitDoc = await getDoc(unitRef);

    if (!unitDoc.exists()) {
      throw new Error('Apartment unit not found');
    }

    const unitData = unitDoc.data();
    if (unitData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Apartment unit not found in current workspace');
    }

    // Check for dependencies before deletion
    const dependencies: { type: string; items: any[] }[] = [];

    // Check for residents in this unit
    const residentsRef = collection(db, 'residents');
    const residentsQuery = query(
      residentsRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('apartmentUnit.id', '==', id)
    );
    const residentsSnapshot = await getDocs(residentsQuery);
    
    if (!residentsSnapshot.empty) {
      const residents = residentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dependencies.push({
        type: 'residents',
        items: residents
      });
    }

    // Check for parking spots assigned to this unit
    const parkingsRef = collection(db, PARKINGS_COLLECTION);
    const parkingsQuery = query(
      parkingsRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('apartmentUnit.id', '==', id)
    );
    const parkingsSnapshot = await getDocs(parkingsQuery);
    
    if (!parkingsSnapshot.empty) {
      const parkings = parkingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dependencies.push({
        type: 'parkings',
        items: parkings
      });
    }

    // If there are dependencies, throw detailed error
    if (dependencies.length > 0) {
      const error = new Error('Cannot delete apartment unit with dependencies');
      (error as any).dependencies = dependencies;
      (error as any).unitNumber = unitData.number;
      throw error;
    }

    const activityRecord = createActivityRecord('deleted', unitData, firestoreUser);

    // Add the activity record before deletion
    await updateDoc(unitRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    await deleteDoc(unitRef);
  } catch (error) {
    console.error('Error deleting apartment unit:', error);
    throw error;
  }
}

export async function getApartmentUnitsByBlock(blockId: string, firestoreUser: User): Promise<{ apartmentUnits: ApartmentUnit[] }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    const mockUnits = await loadMockData<ApartmentUnit[]>('units', firestoreUser.language);
    const filteredUnits = mockUnits.filter(unit => unit.block?.id === blockId);
    return { apartmentUnits: filteredUnits };
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const unitsRef = collection(db, APARTMENT_UNITS_COLLECTION);
    const q = query(
      unitsRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('block.id', '==', blockId)
    );
    const snapshot = await getDocs(q);
    const apartmentUnits = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ApartmentUnit));
    return { apartmentUnits };
  } catch (error) {
    console.error('Error fetching apartment units by block:', error);
    throw new Error('Failed to fetch apartment units');
  }
}

export async function getOccupiedApartmentUnits(firestoreUser: User): Promise<{ apartmentUnits: ApartmentUnit[] }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    const mockUnits = await loadMockData<ApartmentUnit[]>('units', firestoreUser.language);
    const occupiedUnits = mockUnits.filter(unit => unit.status === 'occupied' && unit.resident);
    return { apartmentUnits: occupiedUnits };
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const unitsRef = collection(db, APARTMENT_UNITS_COLLECTION);
    const q = query(
      unitsRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('status', '==', 'occupied')
    );
    const snapshot = await getDocs(q);
    const apartmentUnits = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() } as ApartmentUnit))
      .filter(unit => unit.resident); // Double check resident exists
    
    return { apartmentUnits };
  } catch (error) {
    console.error('Error fetching occupied apartment units:', error);
    throw new Error('Failed to fetch occupied apartment units');
  }
}

// Parking API
export async function getParkings({
  searchTerm,
  pageSize,
  currentPage,
  lastDoc,
  firestoreUser
}: PropertiesQueryParams): Promise<{ parkings: Parking[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    const mockParkings = await loadMockData<Parking[]>('parkings', firestoreUser.language);
    const filteredData = filterMockData(mockParkings, searchTerm);
    const paginatedData = paginateMockData(filteredData, pageSize, currentPage || 1);
    
    // Use the actual filtered data length for total count
    const total = filteredData.length;
    
    return {
      parkings: paginatedData.data,
      lastDoc: undefined,
      total: total
    };
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }
  
    const parkingsRef = collection(db, PARKINGS_COLLECTION);
    const constraints = [where('workspace.id', '==', firestoreUser.workspace.id)];
    
    if (searchTerm) {
      constraints.push(
        where('number', '>=', searchTerm),
        where('number', '<=', searchTerm + '\uf8ff')
      );
    }
    
    let q = query(parkingsRef, ...constraints);
    
    if (pageSize) {
      q = query(q, limit(pageSize));
    }
    
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    const snapshot = await getDocs(q);
    const parkings = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Parking));
    
    // Get total count first
    const countQuery = query(parkingsRef, ...constraints);
    const countSnapshot = await getDocs(countQuery);
    const total = countSnapshot.size;
    
    return {
      parkings,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total: total
    };
  } catch (error) {
    console.error('Error fetching parkings:', error);
    throw new Error('Failed to fetch parkings');
  }
}

export async function getParkingById(id: string, firestoreUser: User): Promise<Parking | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    const mockParkings = await loadMockData<Parking[]>('parkings', firestoreUser.language);
    const mockParking = mockParkings.find(parking => parking.id === id);
    return mockParking || null;
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the parking document directly by ID
    const parkingRef = doc(db, PARKINGS_COLLECTION, id);
    const parkingDoc = await getDoc(parkingRef);
    
    if (!parkingDoc.exists()) {
      return null;
    }

    const parkingData = parkingDoc.data();
    
    // Check if the parking belongs to the current workspace
    if (parkingData.workspace.id !== firestoreUser.workspace.id) {
      return null;
    }

    return { ...parkingData, id: parkingDoc.id } as Parking;
  } catch (error) {
    console.error('Error fetching parking:', error);
    throw new Error('Failed to fetch parking');
  }
}

export async function addParking(parking: Omit<Parking, 'id' | 'activityRecords' | 'workspace'>, firestoreUser: User): Promise<string> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 500));
    return `demo-parking-${Date.now()}`;
  }
  
  try {
    const parkingData = addWorkspaceAndActivity(parking, firestoreUser, 'created');
    const cleanedParking = removeUndefined(parkingData);
    
    const parkingRef = doc(collection(db, PARKINGS_COLLECTION));
    await setDoc(parkingRef, cleanedParking);
    return parkingRef.id;
  } catch (error) {
    console.error('Error adding parking:', error);
    throw new Error('Failed to add parking');
  }
}

export async function updateParking(id: string, data: Partial<Parking>, firestoreUser: User): Promise<void> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  }
  
  try {
    const docRef = doc(db, PARKINGS_COLLECTION, id);
    const parkingDoc = await getDoc(docRef);

    if (!parkingDoc.exists()) {
      throw new Error('Parking not found');
    }

    const existingData = parkingDoc.data() as Parking;
    const activityRecord = createDetailedActivityRecord('updated', existingData, data, firestoreUser);

    // Remove activityRecords from data to avoid conflicts
    const { activityRecords, ...updateData } = data;
    
    // Prepare the update data without activityRecords
    const cleanData = removeUndefined(updateData);
    
    // Add activity record separately
    const updatePayload = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating parking:', error);
    throw new Error('Failed to update parking');
  }
}

export async function deleteParking(id: string, firestoreUser: User): Promise<void> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const parkingRef = doc(db, PARKINGS_COLLECTION, id);
    const parkingDoc = await getDoc(parkingRef);

    if (!parkingDoc.exists()) {
      throw new Error('Parking not found');
    }

    const parkingData = parkingDoc.data();
    if (parkingData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Parking not found in current workspace');
    }

    const activityRecord = createActivityRecord('deleted', parkingData, firestoreUser);

    // Add the activity record before deletion
    await updateDoc(parkingRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    await deleteDoc(parkingRef);
  } catch (error) {
    console.error('Error deleting parking:', error);
    throw new Error('Failed to delete parking');
  }
}

// Amenity API
export async function getAmenities({
  searchTerm,
  pageSize,
  currentPage,
  lastDoc,
  firestoreUser
}: PropertiesQueryParams): Promise<{ amenities: Amenity[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    const mockAmenities = await loadMockData<Amenity[]>('amenities', firestoreUser.language);
    const filteredData = filterMockData(mockAmenities, searchTerm);
    const paginatedData = paginateMockData(filteredData, pageSize, currentPage || 1);
    
    // Use the actual filtered data length for total count
    const total = filteredData.length;
    
    return {
      amenities: paginatedData.data,
      lastDoc: undefined,
      total: total
    };
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }
  
    const amenitiesRef = collection(db, AMENITIES_COLLECTION);
    const constraints = [where('workspace.id', '==', firestoreUser.workspace.id)];
    
    if (searchTerm) {
      constraints.push(
        where('name', '>=', searchTerm),
        where('name', '<=', searchTerm + '\uf8ff')
      );
    }
    
    let q = query(amenitiesRef, ...constraints);
    
    if (pageSize) {
      q = query(q, limit(pageSize));
    }
    
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    const snapshot = await getDocs(q);
    const amenities = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Amenity));
    
    return {
      amenities,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total: amenities.length
    };
  } catch (error) {
    console.error('Error fetching amenities:', error);
    throw new Error('Failed to fetch amenities');
  }
}

export async function getAmenityById(id: string, firestoreUser: User): Promise<Amenity | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    const mockAmenities = await loadMockData<Amenity[]>('amenities', firestoreUser.language);
    const mockAmenity = mockAmenities.find(amenity => amenity.id === id);
    return mockAmenity || null;
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the amenity document directly by ID
    const amenityRef = doc(db, AMENITIES_COLLECTION, id);
    const amenityDoc = await getDoc(amenityRef);
    
    if (!amenityDoc.exists()) {
      return null;
    }

    const amenityData = amenityDoc.data();
    
    // Check if the amenity belongs to the current workspace
    if (amenityData.workspace.id !== firestoreUser.workspace.id) {
      return null;
    }

    return { ...amenityData, id: amenityDoc.id } as Amenity;
  } catch (error) {
    console.error('Error fetching amenity:', error);
    throw new Error('Failed to fetch amenity');
  }
}

export async function addAmenity(amenity: Omit<Amenity, 'id' | 'activityRecords' | 'workspace'>, firestoreUser: User): Promise<string> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 500));
    return `demo-amenity-${Date.now()}`;
  }
  
  try {
    const amenityData = addWorkspaceAndActivity(amenity, firestoreUser, 'created');
    const cleanedAmenity = removeUndefined(amenityData);
    
    const amenityRef = doc(collection(db, AMENITIES_COLLECTION));
    await setDoc(amenityRef, cleanedAmenity);
    return amenityRef.id;
  } catch (error) {
    console.error('Error adding amenity:', error);
    throw new Error('Failed to add amenity');
  }
}

export async function updateAmenity(id: string, data: Partial<Amenity>, firestoreUser: User): Promise<void> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  }
  
  try {
    const docRef = doc(db, AMENITIES_COLLECTION, id);
    const amenityDoc = await getDoc(docRef);

    if (!amenityDoc.exists()) {
      throw new Error('Amenity not found');
    }

    const existingData = amenityDoc.data() as Amenity;
    const activityRecord = createDetailedActivityRecord('updated', existingData, data, firestoreUser);

    // Remove activityRecords from data to avoid conflicts
    const { activityRecords, ...updateData } = data;
    
    // Prepare the update data without activityRecords
    const cleanData = removeUndefined(updateData);
    
    // Add activity record separately
    const updatePayload = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating amenity:', error);
    throw new Error('Failed to update amenity');
  }
}

export async function deleteAmenity(id: string, firestoreUser: User): Promise<void> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const amenityRef = doc(db, AMENITIES_COLLECTION, id);
    const amenityDoc = await getDoc(amenityRef);

    if (!amenityDoc.exists()) {
      throw new Error('Amenity not found');
    }

    const amenityData = amenityDoc.data();
    if (amenityData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Amenity not found in current workspace');
    }

    // Check for dependencies before deletion
    const dependencies: { type: string; items: any[] }[] = [];

    // Check for amenity guidelines that reference this amenity
    const guidelinesRef = collection(db, 'amenity_guidelines');
    const guidelinesQuery = query(
      guidelinesRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('amenity.id', '==', id)
    );
    const guidelinesSnapshot = await getDocs(guidelinesQuery);
    
    if (!guidelinesSnapshot.empty) {
      const guidelines = guidelinesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dependencies.push({
        type: 'amenityGuidelines',
        items: guidelines
      });
    }

    // If there are dependencies, throw detailed error
    if (dependencies.length > 0) {
      const error = new Error('Cannot delete amenity with dependencies');
      (error as any).dependencies = dependencies;
      (error as any).amenityName = amenityData.name;
      throw error;
    }

    const activityRecord = createActivityRecord('deleted', amenityData, firestoreUser);

    // Add the activity record before deletion
    await updateDoc(amenityRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    await deleteDoc(amenityRef);
  } catch (error) {
    console.error('Error deleting amenity:', error);
    throw error;
  }
} 